import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'core/layout/Header';
import MinimalFooter from 'features/feature_home/components/MinimalFooter';
import useCVList from '../hooks/useCVList';
import { HttpService } from 'core/services/http';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import LoginBox from 'features/feature_user/components/LoginBox';
import CVLanding from '../components/CVLanding';
import {
    Box,
    Button,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Stack,
    CircularProgress,
    Alert,
    Chip,
    Paper,
    Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShareIcon from '@mui/icons-material/Share';
import WorkIcon from '@mui/icons-material/Work';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import { CVHttpService } from '../services';
import { Utils } from 'core/utils';
import { useQueryClient, useMutation } from 'react-query';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    } catch {
        return dateString;
    }
};

const isAIGenerated = (description) => {
    if (!description) return false;
    return description.trim().startsWith('#') || description.includes('##');
};

const shouldShowDescription = (description) => {
    return description && !isAIGenerated(description);
};

function CVPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid', default is 'list'
    const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);

    useEffect(() => {
        // Check authentication status
        const checkAuth = () => {
            const token = HttpService.getToken();
            const user = HttpService.getUser();
            const authenticated = !!token && !!user;
            setIsAuthenticated(authenticated);
            setCheckingAuth(false);
        };

        // Check immediately
        checkAuth();

        // Listen for storage events (e.g., after login in another tab or after modal closes)
        window.addEventListener('storage', checkAuth);

        // Also check periodically (in case of same-tab login)
        const interval = setInterval(checkAuth, 1000);

        return () => {
            window.removeEventListener('storage', checkAuth);
            clearInterval(interval);
        };
    }, []);

    // Only load CV list if authenticated
    const { data, isLoading, error } = useCVList(
        { size: 30 },
        { enabled: isAuthenticated && !checkingAuth, retry: false }
    );

    const deleteMutation = useMutation(
        (id) => CVHttpService.deleteCV(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('cvList');
            },
            onError: (err) => {
                alert('Failed to delete CV');
            }
        }
    );

    const handleCreateNew = () => {
        navigate('/cv/builder');
    };

    const handleCreateWithAI = () => {
        navigate('/cv/ai-builder');
    };

    const handleView = (cvId) => {
        navigate(`/cv/${cvId}`);
    };

    const handleEdit = (cvId) => {
        navigate(`/cv/builder/${cvId}`);
    };

    const handleDelete = (cvId, e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this CV?')) {
            deleteMutation.mutate(cvId);
        }
    };

    const handleDownloadPDF = async (cvId, e) => {
        e.stopPropagation();
        try {
            await CVHttpService.downloadPDF(cvId);
        } catch (err) {
            alert('Failed to download PDF');
        }
    };

    const handleShare = async (cv, e) => {
        e.stopPropagation();
        if (!cv.share_uuid) {
            alert('Shareable link not available');
            return;
        }
        const shareUrl = CVHttpService.getShareableURL(cv.share_uuid);
        try {
            await navigator.clipboard.writeText(shareUrl);
            alert('Shareable link copied to clipboard!');
        } catch (err) {
            alert('Failed to copy link');
        }
    };

    const pageWrapper = (children) => (
        <>
            <Header />
            <Box sx={{ minHeight: 'calc(100vh - 56px)', bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', pt: { xs: '60px', md: '68px' } }}>
                <Container maxWidth="lg" sx={{ pt: 3, pb: 4, flex: 1 }}>
                    {children}
                </Container>
            </Box>
            <MinimalFooter />
        </>
    );

    if (checkingAuth) {
        return pageWrapper(
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isAuthenticated) {
        return (
            <>
                <Header />
                <CVLanding onSignIn={() => setLoginModalIsOpen(true)} />
                <CustomModal
                    big
                    key={loginModalIsOpen}
                    setOpen={(value) => setLoginModalIsOpen(value)}
                    open={loginModalIsOpen}
                    showClose={true}
                >
                    <LoginBox onClose={() => setLoginModalIsOpen(false)} />
                </CustomModal>
                <MinimalFooter />
            </>
        );
    }

    return pageWrapper(
        <Stack spacing={3}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                My CVs
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage and create your professional CVs
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(_, v) => v && setViewMode(v)}
                                size="small"
                            >
                                <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
                                <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
                            </ToggleButtonGroup>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AutoAwesomeIcon />}
                                onClick={handleCreateWithAI}
                                disableElevation
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Create with AI
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleCreateNew}
                                disableElevation
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                New CV
                            </Button>
                        </Box>
                    </Box>

                    {/* Content */}
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">
                            Failed to load CVs. Please try again later.
                        </Alert>
                    ) : !data || !data.results || data.results.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 8, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                            <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" gutterBottom fontWeight={600}>
                                No CVs Found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                Create your first professional CV to get started!
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<AddIcon />}
                                onClick={handleCreateNew}
                            >
                                Create Your First CV
                            </Button>
                        </Paper>
                    ) : viewMode === 'list' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {data.results.map((cv) => (
                                <Paper
                                    key={cv.id}
                                    elevation={0}
                                    onClick={() => handleView(cv.id)}
                                    sx={{
                                        cursor: 'pointer',
                                        border: '2px solid transparent',
                                        outline: '1px solid',
                                        outlineColor: 'divider',
                                        borderRadius: 2,
                                        transition: 'border-color 0.15s, outline 0.15s',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            outline: 'none',
                                            '& .cv-actions': { opacity: 1 }
                                        }
                                    }}
                                >
                                    <Box sx={{ p: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {formatText(cv.title)}
                                                </Typography>
                                                {isAIGenerated(cv.description) && (
                                                    <Chip label="AI" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                )}
                                                <Chip
                                                    label={cv.status ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={cv.status ? 'success' : 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatText(`${cv.name || ''} ${cv.last_name || ''}`.trim() || 'No name')}
                                                </Typography>
                                                <Typography variant="caption" color="text.disabled">
                                                    Updated {formatDate(cv.updated_at)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Stack
                                            className="cv-actions"
                                            direction="row"
                                            spacing={0}
                                            sx={{ flexShrink: 0, opacity: 0, transition: 'opacity 0.15s' }}
                                        >
                                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleView(cv.id); }} title="View">
                                                <VisibilityIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(cv.id); }} title="Edit">
                                                <EditIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                            {cv.share_uuid && (
                                                <IconButton size="small" color="success" onClick={(e) => handleShare(cv, e)} title="Copy share link">
                                                    <ShareIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            )}
                                            {cv.share_uuid && (
                                                <IconButton size="small" color="info" onClick={(e) => handleDownloadPDF(cv.id, e)} title="Download PDF">
                                                    <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            )}
                                            <IconButton size="small" color="error" onClick={(e) => handleDelete(cv.id, e)} title="Delete">
                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {data.results.map((cv) => (
                                <Grid item xs={12} sm={6} md={4} key={cv.id}>
                                    <Card
                                        elevation={0}
                                        onClick={() => handleView(cv.id)}
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            cursor: 'pointer',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            transition: 'border-color 0.15s, box-shadow 0.15s',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ flex: 1, p: 2, '&:last-child': { pb: 0 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5, mb: 0.5 }}>
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                                                    {formatText(cv.title)}
                                                </Typography>
                                                {isAIGenerated(cv.description) && (
                                                    <Chip label="AI" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }} />
                                                )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {formatText(`${cv.name || ''} ${cv.last_name || ''}`.trim() || 'No name')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                                                <Chip
                                                    label={cv.status ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={cv.status ? 'success' : 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                                />
                                                {cv.share_uuid && (
                                                    <Chip label="Shareable" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                )}
                                            </Box>
                                        </CardContent>
                                        <CardActions sx={{ px: 1.5, pb: 1.5, pt: 1, justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="caption" color="text.disabled">
                                                {formatDate(cv.updated_at)}
                                            </Typography>
                                            <Stack direction="row" spacing={0}>
                                                <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleView(cv.id); }} title="View">
                                                    <VisibilityIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(cv.id); }} title="Edit">
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                                {cv.share_uuid && (
                                                    <IconButton size="small" color="success" onClick={(e) => handleShare(cv, e)} title="Share">
                                                        <ShareIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                                <IconButton size="small" color="error" onClick={(e) => handleDelete(cv.id, e)} title="Delete">
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Stack>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
        </Stack>
    );
}

export default CVPage;
